import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const base = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (!token) {
      setError('Missing reset token. Open the link from your email.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post<{ success?: boolean; message?: string }>(
        `${base}/auth/reset-password`,
        { token, password },
        { headers: { 'Content-Type': 'application/json' } },
      );
      setMessage(data.message || 'Your password has been updated.');
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object'
          ? String((err.response.data as { message?: string }).message || (err.response.data as { error?: string }).error || 'Request failed')
          : 'Request failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="hero-background"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontFamily: 'var(--font-family)',
      }}
    >
      <div
        className="card"
        style={{
          textAlign: 'center',
          maxWidth: 400,
          width: '90%',
          padding: '48px 32px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 12,
        }}
      >
        <h1 style={{ marginBottom: 8, color: 'var(--secondary-color)' }}>Set new password</h1>
        <p style={{ color: '#6c757d', marginBottom: 24, textAlign: 'left', fontSize: '0.95rem' }}>
          Choose a new password (at least 8 characters).
        </p>

        {message && (
          <div>
            <p style={{ color: '#155724', background: '#d4edda', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              {message}
            </p>
            <Link
              to="/login"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                fontWeight: 600,
                border: '1px solid #FC5003',
                color: '#FC5003',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              Sign in
            </Link>
          </div>
        )}

        {!message && (
          <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
            {!token && (
              <p style={{ color: '#721c24', marginBottom: 16 }}>Invalid or missing reset link.</p>
            )}
            <label
              htmlFor="reset-password"
              style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: 'var(--secondary-color)' }}
            >
              New password
            </label>
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid #ced4da',
                fontSize: '1rem',
                boxSizing: 'border-box',
                marginBottom: 16,
              }}
            />
            <label
              htmlFor="reset-confirm"
              style={{ display: 'block', marginBottom: 6, fontWeight: 600, color: 'var(--secondary-color)' }}
            >
              Confirm password
            </label>
            <input
              id="reset-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid #ced4da',
                fontSize: '1rem',
                boxSizing: 'border-box',
                marginBottom: 24,
              }}
            />
            {error && (
              <p style={{ color: '#721c24', background: '#f8d7da', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={submitting || !token}
              style={{
                width: '100%',
                padding: '16px 24px',
                fontSize: '1.1rem',
                fontWeight: 600,
                cursor: submitting || !token ? 'not-allowed' : 'pointer',
                opacity: submitting || !token ? 0.6 : 1,
                border: '1px solid #FC5003',
                color: '#FC5003',
                background: 'transparent',
                borderRadius: 8,
              }}
            >
              {submitting ? 'Saving…' : 'Update password'}
            </button>
          </form>
        )}

        {!message && (
          <Link
            to="/login"
            style={{ display: 'inline-block', marginTop: 24, color: '#6c757d', fontSize: '0.95rem' }}
          >
            Back to sign in
          </Link>
        )}
      </div>
    </div>
  );
}
